import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Input, Select } from '../components/ui';
import { CheckCircleIcon, AcademicCapIcon, UsersIcon, LightBulbIcon } from '@heroicons/react/24/solid';
import api from '../services/api';
import axios from 'axios';

const ORG_SIZES = [
  { value: 'small',      label: 'Small (under 10 employees)' },
  { value: 'medium',     label: 'Medium (10–50 employees)' },
  { value: 'large',      label: 'Large (50–250 employees)' },
  { value: 'enterprise', label: 'Enterprise (250+ employees)' },
];

const TIME_OPTIONS = [
  { value: '09:00', label: 'Morning — 9:00am' },
  { value: '14:00', label: 'Afternoon — 2:00pm' },
  { value: 'custom', label: 'Specific time...' },
];

function StepIndicator({ step, total, labels }) {
  return (
    <div className="flex items-center gap-2 mb-8">
      {Array.from({ length: total }, (_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold transition-colors ${
            i + 1 === step
              ? 'bg-brand-primary text-white'
              : i + 1 < step
                ? 'bg-brand-primary/20 text-brand-primary'
                : 'bg-surface-hover dark:bg-surface-dark text-text-muted'
          }`}>
            {i + 1}
          </div>
          {labels && (
            <span className={`text-xs hidden sm:inline ${i + 1 === step ? 'text-text-primary dark:text-text-primary font-medium' : 'text-text-muted'}`}>
              {labels[i]}
            </span>
          )}
          {i < total - 1 && (
            <div className={`h-px w-6 sm:w-12 ${i + 1 < step ? 'bg-brand-primary/40' : 'bg-border'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function FieldError({ message }) {
  if (!message) return null;
  return <p className="text-xs text-danger mt-1">{message}</p>;
}

function ReviewRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-4 py-2 border-b border-border last:border-0">
      <span className="text-xs text-text-muted w-36 flex-shrink-0">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

const baseUrl = process.env.REACT_APP_API_URL || '/api';

export default function BookWorkshop() {
  const { user, permissions } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [showLanding, setShowLanding] = useState(true);

  const isPortalUser = !!(permissions?.clientMode && permissions?.clientCompanyId);

  const { data: metadata } = useQuery({
    queryKey: ['booking-metadata'],
    queryFn: async () => {
      const res = await axios.get(`${baseUrl}/booking/metadata`);
      return res.data;
    },
    staleTime: 10 * 60 * 1000,
  });

  const { data: existingCompany } = useQuery({
    queryKey: ['clients', permissions?.clientCompanyId],
    queryFn: async () => {
      const res = await api.get(`/clients/${permissions.clientCompanyId}`);
      return res.data;
    },
    enabled: isPortalUser && !!permissions?.clientCompanyId,
    staleTime: 5 * 60 * 1000,
  });

  const [step, setStep] = useState(1);
  const [customTime, setCustomTime] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const totalSteps = isPortalUser ? 2 : 4;
  const stepLabels = isPortalUser
    ? ['Workshop Details', 'Review & Confirm']
    : ['Workshop Details', 'Your Organisation', 'Contact & Billing', 'Review & Submit'];

  const [form, setForm] = useState({
    preferredDate: '',
    preferredTime: '09:00',
    location: '',
    notes: '',
    organisationName: '',
    industry: '',
    village: '',
    organisationSize: '',
    website: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    jobTitle: '',
    billingName: '',
    billingEmail: '',
    billingAddress: '',
    vatNumber: '',
  });

  useEffect(() => {
    if (isPortalUser && existingCompany) {
      setForm((f) => ({
        ...f,
        organisationName: existingCompany.name || '',
        industry: existingCompany.industry || '',
        village: existingCompany.village || '',
        organisationSize: existingCompany.organisationSize || '',
        website: existingCompany.website || '',
        billingName: existingCompany.billingName || existingCompany.name || '',
        billingEmail: existingCompany.billingEmail || '',
        billingAddress: existingCompany.billingAddress || '',
        vatNumber: existingCompany.vatNumber || '',
        contactName: user?.displayName || '',
        contactEmail: user?.email || '',
      }));
    }
  }, [isPortalUser, existingCompany, user]);

  const set = (field) => (e) => {
    const val = e.target ? e.target.value : e;
    setForm((f) => {
      const updated = { ...f, [field]: val };
      if (field === 'organisationName' && !f._billingNameTouched) updated.billingName = val;
      if (field === 'billingName') updated._billingNameTouched = true;
      if (field === 'contactEmail' && !f._billingEmailTouched) updated.billingEmail = val;
      if (field === 'billingEmail') updated._billingEmailTouched = true;
      return updated;
    });
    if (errors[field]) setErrors((e) => ({ ...e, [field]: '' }));
  };

  const effectiveTime = form.preferredTime === 'custom' ? customTime : form.preferredTime;

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
  const isValidUrl = (v) => {
    if (!v.trim()) return true;
    try { new URL(v.trim().startsWith('http') ? v.trim() : `https://${v.trim()}`); return true; } catch { return false; }
  };
  const isValidPhone = (v) => {
    if (!v.trim()) return true;
    return /^[+\d][\d\s\-().]{6,19}$/.test(v.trim());
  };

  const validate = (currentStep) => {
    const errs = {};
    if (currentStep === 1) {
      if (!form.preferredDate) {
        errs.preferredDate = 'Please select a date';
      } else {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        if (new Date(form.preferredDate) < today) errs.preferredDate = 'Date must be today or in the future';
      }
      if (!form.preferredTime) errs.preferredTime = 'Please select a time';
      if (form.preferredTime === 'custom') {
        if (!customTime) errs.customTime = 'Please enter a time';
        else if (!/^\d{2}:\d{2}$/.test(customTime)) errs.customTime = 'Enter a valid time (HH:MM)';
      }
      if (!form.location.trim()) errs.location = 'Please enter a venue or location';
      else if (form.location.trim().length < 3) errs.location = 'Location must be at least 3 characters';
    }
    if (!isPortalUser) {
      if (currentStep === 2) {
        if (!form.organisationName.trim()) errs.organisationName = 'Please enter your organisation name';
        else if (form.organisationName.trim().length < 2) errs.organisationName = 'Organisation name must be at least 2 characters';
        if (!form.industry) errs.industry = 'Please select an industry';
        if (!form.village) errs.village = 'Please select a village or town';
        if (!form.organisationSize) errs.organisationSize = 'Please select an organisation size';
        if (form.website && !isValidUrl(form.website)) errs.website = 'Please enter a valid website URL (e.g. https://example.com)';
      }
      if (currentStep === 3) {
        if (!form.contactName.trim()) errs.contactName = 'Please enter your full name';
        else if (form.contactName.trim().length < 2) errs.contactName = 'Name must be at least 2 characters';
        if (!form.contactEmail.trim()) errs.contactEmail = 'Please enter your email address';
        else if (!isValidEmail(form.contactEmail)) errs.contactEmail = 'Please enter a valid email address';
        if (form.contactPhone && !isValidPhone(form.contactPhone)) errs.contactPhone = 'Please enter a valid phone number';
        if (!form.billingName.trim()) errs.billingName = 'Please enter a billing name';
        else if (form.billingName.trim().length < 2) errs.billingName = 'Billing name must be at least 2 characters';
        if (!form.billingEmail.trim()) errs.billingEmail = 'Please enter a billing email address';
        else if (!isValidEmail(form.billingEmail)) errs.billingEmail = 'Please enter a valid billing email address';
        if (!form.billingAddress.trim()) errs.billingAddress = 'Please enter a billing address';
        else if (form.billingAddress.trim().length < 5) errs.billingAddress = 'Please enter a full billing address';
      }
    }
    return errs;
  };

  const next = () => {
    const errs = validate(step);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setStep((s) => s + 1);
  };

  const back = () => setStep((s) => s - 1);

  const submit = async () => {
    const errs = validate(step - 1);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, preferredTime: effectiveTime, existingCompanyId: isPortalUser ? permissions.clientCompanyId : null };
      delete payload._billingNameTouched;
      delete payload._billingEmailTouched;
      await axios.post(`${baseUrl}/bookings`, payload);
      setSubmitted(true);
    } catch (err) {
      setErrors({ submit: 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const villageOptions = useMemo(() => (metadata?.villages || []).map((v) => ({ value: v, label: v })), [metadata]);
  const industryOptions = useMemo(() => (metadata?.industries || []).map((i) => ({ value: i, label: i })), [metadata]);

  // ── Landing screen ───────────────────────────────────────────────
  if (showLanding) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-xl text-center">
          <img
            src={theme === 'light' ? '/logolight.png' : '/logo.png'}
            alt="Prism"
            className="w-40 mx-auto mb-10"
          />
          <p className="text-xs font-medium uppercase tracking-wider text-text-muted mb-3">
            AI Workshops
          </p>
          <h1 className="text-3xl font-bold text-text-primary-light dark:text-text-primary mb-4">
            Build your team's AI capability
          </h1>
          <p className="text-base text-text-secondary-light dark:text-text-secondary mb-10 max-w-md mx-auto">
            Tailored, hands-on workshops that help your organisation understand, adopt, and get real value from AI.
          </p>
          <div className="grid grid-cols-3 gap-4 mb-10 text-left">
            {[
              { icon: AcademicCapIcon, title: 'Hands-on learning', desc: 'Practical sessions, not slide decks' },
              { icon: UsersIcon,       title: 'Built for your team', desc: 'Tailored to your industry and size' },
              { icon: LightBulbIcon,  title: 'Actionable outcomes', desc: 'Leave with a clear AI roadmap' },
            ].map(({ icon: Icon, title, desc }) => (
              <Card key={title} padding="sm">
                <Icon className="w-4 h-4 text-brand-primary mb-2" />
                <p className="text-sm font-medium mb-0.5">{title}</p>
                <p className="text-xs text-text-muted">{desc}</p>
              </Card>
            ))}
          </div>
          <Button variant="primary" size="lg" onClick={() => setShowLanding(false)}>
            Book a Workshop
          </Button>
        </div>
      </div>
    );
  }

  // ── Confirmation screen ──────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-lg text-center">
          <div className="w-14 h-14 rounded-full bg-success flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-lg font-semibold tracking-tight mb-2">Booking Request Received</h1>
          <p className="text-sm text-text-muted mb-6">
            Thank you! We'll review your request and be in touch within 2 business days to confirm your workshop.
          </p>
          {isPortalUser ? (
            <Button variant="secondary" onClick={() => navigate(`/portal/${permissions.clientCompanyId}`)}>
              Back to Portal
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => navigate('/')}>
              Back to Home
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <StepIndicator step={step} total={totalSteps} labels={stepLabels} />

        {/* ── Step 1: Workshop Preferences ─────────────────────────── */}
        {step === 1 && (
          <Card padding="lg">
            <h2 className="text-base font-semibold mb-4">Workshop Preferences</h2>
            <div className="flex flex-col gap-4">
              <div>
                <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">
                  Preferred Date <span className="text-danger">*</span>
                </label>
                <Input type="date" value={form.preferredDate} onChange={set('preferredDate')} min={new Date().toISOString().slice(0, 10)} className="w-full" />
                <FieldError message={errors.preferredDate} />
              </div>
              <div>
                <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">
                  Preferred Time <span className="text-danger">*</span>
                </label>
                <Select value={form.preferredTime} onChange={set('preferredTime')} options={TIME_OPTIONS} className="w-full" />
                <FieldError message={errors.preferredTime} />
              </div>
              {form.preferredTime === 'custom' && (
                <div>
                  <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">
                    Specific Time <span className="text-danger">*</span>
                  </label>
                  <Input type="time" value={customTime} onChange={(e) => setCustomTime(e.target.value)} className="w-full" />
                  <FieldError message={errors.customTime} />
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">
                  Venue / Location <span className="text-danger">*</span>
                </label>
                <Input type="text" placeholder="e.g. Seytravel Office, Valletta" value={form.location} onChange={set('location')} className="w-full" />
                <FieldError message={errors.location} />
              </div>
              <div>
                <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">
                  Additional Notes
                </label>
                <textarea
                  value={form.notes}
                  onChange={set('notes')}
                  rows={3}
                  placeholder="Any special requirements or questions..."
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-brand-primary"
                />
              </div>
            </div>
          </Card>
        )}

        {/* ── Step 2 (anon): Organisation ──────────────────────────── */}
        {!isPortalUser && step === 2 && (
          <Card padding="lg">
            <h2 className="text-base font-semibold mb-4">Your Organisation</h2>
            <div className="flex flex-col gap-4">
              <div>
                <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">
                  Organisation Name <span className="text-danger">*</span>
                </label>
                <Input type="text" placeholder="Your company name" value={form.organisationName} onChange={set('organisationName')} className="w-full" />
                <FieldError message={errors.organisationName} />
              </div>
              <div>
                <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">
                  Industry <span className="text-danger">*</span>
                </label>
                <Select value={form.industry} onChange={set('industry')} options={[{ value: '', label: 'Select industry...' }, ...industryOptions]} className="w-full" />
                <FieldError message={errors.industry} />
              </div>
              <div>
                <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">
                  Village / Town <span className="text-danger">*</span>
                </label>
                <Select value={form.village} onChange={set('village')} options={[{ value: '', label: 'Select village/town...' }, ...villageOptions]} className="w-full" />
                <FieldError message={errors.village} />
              </div>
              <div>
                <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">
                  Organisation Size <span className="text-danger">*</span>
                </label>
                <Select value={form.organisationSize} onChange={set('organisationSize')} options={[{ value: '', label: 'Select size...' }, ...ORG_SIZES]} className="w-full" />
                <FieldError message={errors.organisationSize} />
              </div>
              <div>
                <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">
                  Website
                </label>
                <Input type="url" placeholder="https://yourcompany.com" value={form.website} onChange={set('website')} className="w-full" />
              </div>
            </div>
          </Card>
        )}

        {/* ── Step 3 (anon): Contact & Billing ─────────────────────── */}
        {!isPortalUser && step === 3 && (
          <Card padding="lg">
            <h2 className="text-base font-semibold mb-1">Contact Details</h2>
            <p className="text-xs text-text-muted mb-4">The person responsible for this booking.</p>
            <div className="flex flex-col gap-4 mb-6">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">Full Name <span className="text-danger">*</span></label>
                  <Input type="text" value={form.contactName} onChange={set('contactName')} className="w-full" />
                  <FieldError message={errors.contactName} />
                </div>
                <div>
                  <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">Job Title</label>
                  <Input type="text" value={form.jobTitle} onChange={set('jobTitle')} className="w-full" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">Email Address <span className="text-danger">*</span></label>
                <Input type="email" value={form.contactEmail} onChange={set('contactEmail')} className="w-full" />
                <FieldError message={errors.contactEmail} />
              </div>
              <div>
                <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">Phone Number</label>
                <Input type="tel" value={form.contactPhone} onChange={set('contactPhone')} className="w-full" />
              </div>
            </div>
            <h2 className="text-base font-semibold mb-1">Billing Details</h2>
            <p className="text-xs text-text-muted mb-4">For invoice purposes. No payment is taken at this stage.</p>
            <div className="flex flex-col gap-4">
              <div>
                <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">Billing Name <span className="text-danger">*</span></label>
                <Input type="text" value={form.billingName} onChange={set('billingName')} className="w-full" />
                <FieldError message={errors.billingName} />
              </div>
              <div>
                <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">Billing Email <span className="text-danger">*</span></label>
                <Input type="email" value={form.billingEmail} onChange={set('billingEmail')} className="w-full" />
                <FieldError message={errors.billingEmail} />
              </div>
              <div>
                <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">Billing Address <span className="text-danger">*</span></label>
                <textarea value={form.billingAddress} onChange={set('billingAddress')} rows={3} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-brand-primary" />
                <FieldError message={errors.billingAddress} />
              </div>
              <div>
                <label className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-1">VAT Number</label>
                <Input type="text" placeholder="MT12345678" value={form.vatNumber} onChange={set('vatNumber')} className="w-full" />
              </div>
            </div>
          </Card>
        )}

        {/* ── Review step ───────────────────────────────────────────── */}
        {step === totalSteps && (
          <Card padding="lg">
            <h2 className="text-base font-semibold mb-4">{isPortalUser ? 'Review & Confirm' : 'Review & Submit'}</h2>
            <div className="mb-4">
              <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">Workshop</p>
              <ReviewRow label="Date" value={form.preferredDate} />
              <ReviewRow label="Time" value={effectiveTime} />
              <ReviewRow label="Location" value={form.location} />
              <ReviewRow label="Notes" value={form.notes} />
            </div>
            <div className="mb-4">
              <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">Organisation</p>
              <ReviewRow label="Name" value={form.organisationName} />
              <ReviewRow label="Industry" value={form.industry} />
              <ReviewRow label="Village" value={form.village} />
              <ReviewRow label="Size" value={ORG_SIZES.find((s) => s.value === form.organisationSize)?.label} />
              <ReviewRow label="Website" value={form.website} />
            </div>
            <div className="mb-4">
              <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">Contact</p>
              <ReviewRow label="Name" value={form.contactName} />
              <ReviewRow label="Email" value={form.contactEmail} />
              <ReviewRow label="Phone" value={form.contactPhone} />
              <ReviewRow label="Job Title" value={form.jobTitle} />
            </div>
            <div className="mb-2">
              <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">Billing</p>
              <ReviewRow label="Billing Name" value={form.billingName} />
              <ReviewRow label="Billing Email" value={form.billingEmail} />
              <ReviewRow label="Address" value={form.billingAddress} />
              <ReviewRow label="VAT Number" value={form.vatNumber} />
            </div>
            {errors.submit && <p className="text-xs text-danger mt-3">{errors.submit}</p>}
          </Card>
        )}

        {/* ── Navigation ────────────────────────────────────────────── */}
        <div className="flex justify-between mt-4">
          {step > 1 ? (
            <Button variant="secondary" onClick={back}>Back</Button>
          ) : (
            <Button variant="secondary" onClick={() => setShowLanding(true)}>Back</Button>
          )}
          {step < totalSteps ? (
            <Button variant="primary" onClick={next}>Next</Button>
          ) : (
            <Button variant="primary" onClick={submit} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Booking Request'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
